/**
 * Phase 2 — multi-keyframe sequencing + branching scenario Brain tools.
 *
 * Two pure-draft tools (no side effects in the dispatcher; mirror the
 * Phase 1 `draft_tactics_pattern` dispatch in `brainTools.ts`):
 *
 *   - draft_tactics_sequence — author a sequence of keyframes (with
 *     optional branches) and merge into the active layout. Replaces
 *     v=1 single-snapshot behaviour with a story: build-up → switch
 *     → final third, each with its own positions + arrows.
 *
 *   - draft_tactics_branch — author a single alternate branch off a
 *     keyframe already on the board. The Brain reads the parent
 *     keyframe id from screen context (the active layout flows through
 *     the same channel as Phase 1's critique tool).
 *
 * Coordinate caps mirror Phase 1: x ∈ [-34, +34], z ∈ [-52.5, +52.5].
 * Clamping is applied on commit (`apps/web2/src/lib/brainTactics.ts`)
 * so hallucinated coordinates never escape onto the pitch.
 */
import type { AnthropicTool } from '@4cm/core/ai';

// Schemas describe player-spots / arrows loosely on the wire — the
// commit handler in apps/web2 tightens them to TacticsPlayerSpot /
// TacticsArrow. Same loose-typing pattern as Phase 1's draft_tactics_
// pattern tool, so the Brain can produce valid JSON without micro-
// differences breaking the apply path.

const SPOT_SCHEMA = {
  type: 'object',
  properties: {
    role: { type: 'string' },
    x: { type: 'number', description: 'Sideline axis, world metres.' },
    z: { type: 'number', description: 'Goal-line axis, world metres.' },
  },
  required: ['role', 'x', 'z'],
} as const;

const ARROW_SCHEMA = {
  type: 'object',
  properties: {
    points: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          x: { type: 'number' },
          z: { type: 'number' },
        },
        required: ['x', 'z'],
      },
    },
    color: {
      type: 'string',
      description:
        'CSS hex. Conventions: #4ade80 home green, #fbbf24 away amber, #f5f5f5 neutral.',
    },
    player_key: { type: 'string', description: 'Optional binding, e.g. "home-7".' },
  },
  required: ['points'],
} as const;

const KEYFRAME_SCHEMA = {
  type: 'object',
  description:
    'A named moment in the play. Positions interpolate between adjacent ' +
    'keyframes in order of `t`. Each keyframe is a full pitch snapshot — ' +
    'omit `home_spots` / `away_spots` to inherit the previous keyframe\'s ' +
    'positions for those tokens (the commit handler back-fills).',
  properties: {
    id: { type: 'string', description: 'Stable id (e.g. "k0", "k1").' },
    t: {
      type: 'number',
      description: 'Position along the sequence, in [0, 1]. k0 should be 0; final keyframe should be 1.',
    },
    label: { type: 'string', description: 'Human-readable moment label, e.g. "Build-up".' },
    home_spots: { type: 'array', items: SPOT_SCHEMA },
    away_spots: { type: 'array', items: SPOT_SCHEMA },
    arrows: { type: 'array', items: ARROW_SCHEMA },
  },
  required: ['id', 't', 'label'],
} as const;

export const TACTICS_SEQUENCE_DRAFT_TOOL: AnthropicTool = {
  name: 'draft_tactics_sequence',
  description:
    'Propose a multi-keyframe tactics sequence as a preview card for coach ' +
    'approval. Does NOT save. Use when the coach asks for a play that ' +
    'unfolds over time — "build-up to a switch to a finish", "press from ' +
    'the right then recover into a 4-4-2 mid-block", etc. Each keyframe ' +
    'carries positions + arrows; playback interpolates between them. ' +
    'Optionally include branches that fork off named keyframes ("if press ' +
    'wins it, then…"). World coords: x ∈ [-34, +34] sideline, z ∈ [-52.5, ' +
    '+52.5] goal-line; clamped on apply.',
  input_schema: {
    type: 'object',
    properties: {
      persist_key: {
        type: 'string',
        description:
          'localStorage key for the target board. Defaults to the active board from screen context.',
      },
      home_formation: {
        type: 'string',
        description: 'Formation at the start of the sequence, e.g. "4-3-3".',
      },
      away_formation: { type: 'string' },
      keyframes: {
        type: 'array',
        description:
          'At least 2 keyframes (start + end). Ordered ascending by `t`. ' +
          'First should be t=0, last t=1.',
        items: KEYFRAME_SCHEMA,
        minItems: 2,
      },
      branches: {
        type: 'array',
        description:
          'Optional alternate scenarios. Each branch forks from a ' +
          'parent_keyframe_id (must match an `id` in `keyframes`) and ' +
          'plays its own sequence of keyframes after that fork point.',
        items: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            parent_keyframe_id: { type: 'string' },
            trigger: {
              type: 'string',
              description: 'Coach-visible label — "if press wins it", "if pass intercepted".',
            },
            keyframes: { type: 'array', items: KEYFRAME_SCHEMA, minItems: 1 },
          },
          required: ['id', 'parent_keyframe_id', 'trigger', 'keyframes'],
        },
      },
      intent: {
        type: 'string',
        description:
          'One-sentence description of what this sequence accomplishes — shown on the preview card so the coach knows what they\'re approving.',
      },
    },
    required: ['keyframes', 'intent'],
  },
};

export const TACTICS_BRANCH_DRAFT_TOOL: AnthropicTool = {
  name: 'draft_tactics_branch',
  description:
    'Propose a single alternate branch off an existing keyframe as a ' +
    'preview card. Does NOT save. Use when the coach asks for a ' +
    '"plan B" or "what if X happens" scenario rooted in a play the ' +
    'Brain has already seen on the board. The parent_keyframe_id must ' +
    'match a keyframe in the current layout (visible via screen ' +
    'context); do not invent ids.',
  input_schema: {
    type: 'object',
    properties: {
      persist_key: {
        type: 'string',
        description: 'Defaults to the active board from screen context.',
      },
      id: { type: 'string', description: 'Stable id for the new branch.' },
      parent_keyframe_id: {
        type: 'string',
        description:
          'Id of the keyframe in the existing layout that this branch forks off.',
      },
      trigger: {
        type: 'string',
        description: 'Coach-visible label — "if press wins it".',
      },
      keyframes: {
        type: 'array',
        description: 'The alternate sequence; at least 1 keyframe.',
        items: KEYFRAME_SCHEMA,
        minItems: 1,
      },
      intent: {
        type: 'string',
        description: 'One-sentence description shown on the preview card.',
      },
    },
    required: ['id', 'parent_keyframe_id', 'trigger', 'keyframes', 'intent'],
  },
};

export const TACTICS_SEQUENCE_TOOLS: AnthropicTool[] = [
  TACTICS_SEQUENCE_DRAFT_TOOL,
  TACTICS_BRANCH_DRAFT_TOOL,
];

/** Names registered for the dispatcher's draft-tool switch. */
export const TACTICS_SEQUENCE_TOOL_NAMES = new Set<string>([
  'draft_tactics_sequence',
  'draft_tactics_branch',
]);
